import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, CanActivate, CanLoad, CanActivateChild } from '@angular/router';
import { LaunchDarklyService } from '@lc/core/lib/services';
import { AuthenticationService } from '../services/authentication.service';

@Injectable()
export class AuthGuard implements CanActivate, CanLoad, CanActivateChild {
  private authService = inject(AuthenticationService);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const isAuthenticated = await this.authService.isAuthenticated$();
    if (isAuthenticated) {
      // NOTE: We have to initialize launch darkly in this auth guard because anytime that a user is authenticated,
      // feature flags should be available. Anytime the user is not authenticated, flags should not be available.
      // By initializing in this guard, it guarantees that every child component of an authenticated route is able
      // to check feature flag status immediately, and expect that the initial flag values are exaclty as they are
      // defined in launch darkly.
      // This also has the benefit of preventing features from toggling on/off once LD responds with the flag status
      // but after the app has already rendered them with the wrong status!
      await this.launchDarklyService.initialize();

      // Logged in so return true
      return true;
    }

    // Not logged in so redirect to login page
    const postLoginRedirectTo = state.url;
    localStorage.removeItem('post-login-redirect');
    if (postLoginRedirectTo) {
      localStorage.setItem('post-login-redirect', state.url);
    }

    this.authService.redirectToLogin();
    return false;
  }

  async canLoad(): Promise<boolean> {
    return await this.isAuthenticated$();
  }

  async canActivateChild(): Promise<boolean> {
    return await this.isAuthenticated$();
  }

  private async isAuthenticated$(): Promise<boolean> {
    return await this.authService.isAuthenticated$();
  }
}
